import React, { useState } from 'react';
import { useMutation, gql } from '@apollo/client';
import { formatDate } from '../utils/formatDate';

const INSERT_ACUSADO = gql`
  mutation InsertAcusado($object: acusados_insert_input!) {
    insert_acusados_one(object: $object) {
      id
      nombre_completo
      cedula
      grado
      unidad
      estado_actual
      direccion
      estado_civil
      edad
      fecha_nacimiento
      foto
      created_at
      updated_at
    }
  }
`;

const mapAcusado = (row) => {
  const fecha = row.updated_at || row.created_at || '';
  return {
    acusado_id: row.id,
    nombre: row.nombre_completo,
    cedula: row.cedula,
    grado: row.grado,
    unidad: row.unidad,
    estado_procesal: row.estado_actual,
    direccion: row.direccion,
    estado_civil: row.estado_civil,
    edad: row.edad,
    fecha_nacimiento: row.fecha_nacimiento,
    foto: row.foto,
    fecha_actualizacion: formatDate(fecha),
  };
};

export default function RegistrarAcusado({ onAgregarAcusado, onVolver, grados = [] }) {
  const [nuevoAcusado, setNuevoAcusado] = useState({
    nombre: '',
    grado: '',
    unidad: '',
    cedula: '',
    direccion: '',
    foto: '', // almacenaremos un data URL para preview simple
    estado_civil: '',
    edad: '',
    fecha_nacimiento: '',
  });
  const [preview, setPreview] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [fotoError, setFotoError] = useState('');
  const [error, setError] = useState('');
  const [insertAcusado, { loading: saving }] = useMutation(INSERT_ACUSADO);

  const calcularEdad = (fechaStr) => {
    if (!fechaStr) return '';
    const hoy = new Date();
    const fn = new Date(fechaStr);
    if (isNaN(fn.getTime())) return '';
    let edad = hoy.getFullYear() - fn.getFullYear();
    const m = hoy.getMonth() - fn.getMonth();
    if (m < 0 || (m === 0 && hoy.getDate() < fn.getDate())) {
      edad--;
    }
    return Math.max(0, edad);
  };

  const handleAgregarAcusado = async (e) => {
    e.preventDefault();
    if (!nuevoAcusado.nombre || !nuevoAcusado.grado || !nuevoAcusado.unidad || !nuevoAcusado.cedula) return;
    const payload = {
      nombre_completo: nuevoAcusado.nombre.trim().toUpperCase(),
      cedula: nuevoAcusado.cedula.replace(/\./g, '').trim(),
      grado: nuevoAcusado.grado,
      unidad: nuevoAcusado.unidad,
      estado_actual: 'en_investigacion',
      direccion: nuevoAcusado.direccion || null,
      estado_civil: nuevoAcusado.estado_civil || null,
      edad: nuevoAcusado.edad ? Number(nuevoAcusado.edad) : null,
      fecha_nacimiento: nuevoAcusado.fecha_nacimiento || null,
      foto: nuevoAcusado.foto || null,
    };

    try {
      const res = await insertAcusado({ variables: { object: payload } });
      if (res.data?.insert_acusados_one) {
        const creado = mapAcusado(res.data.insert_acusados_one);
        onAgregarAcusado && onAgregarAcusado(creado);
      }
      setError('');
    } catch (err) {
      setError('No se pudo registrar el acusado.');
    } finally {
      setNuevoAcusado({ nombre: '', grado: '', unidad: '', cedula: '', direccion: '', foto: '', estado_civil: '', edad: '', fecha_nacimiento: '' });
      setPreview('');
      setFotoError('');
    }
  };

  const handleFotoChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    // Validaciones básicas
    const validTypes = ['image/jpeg','image/png','image/webp'];
    if (!validTypes.includes(file.type)) {
      setFotoError('Formato no soportado. Usa JPG, PNG o WEBP.');
      return;
    }
    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      setFotoError('La imagen supera 2MB.');
      return;
    }
    setFotoError('');
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setPreview(dataUrl);
      setNuevoAcusado(prev => ({ ...prev, foto: dataUrl }));
    };
    reader.readAsDataURL(file);
  };

  const limpiarFoto = () => {
    setPreview('');
    setNuevoAcusado(prev => ({ ...prev, foto: '' }));
    setFotoError('');
  };

  return (
    <div className="container py-4" style={{ maxWidth: 900 }}>
      <button className="btn btn-outline-primary btn-volver mb-3" onClick={onVolver}>
        <i className="bi bi-arrow-left"></i>
        <span>Volver</span>
      </button>
      <div className="card mb-3">
        <div className="card-header">Registrar nuevo acusado</div>
        <div className="card-body">
          <form onSubmit={handleAgregarAcusado} className="row g-3">
            {/* Foto primero */}
            <div className="col-12">
              <label className="form-label">Foto</label>
              <div
                className={`border rounded p-3 text-center ${dragOver ? 'bg-light' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); const file = e.dataTransfer.files && e.dataTransfer.files[0]; if (file) handleFotoChange({ target: { files: [file] } }); }}
              >
                {!preview ? (
                  <>
                    <input className="form-control" type="file" accept="image/*" capture="environment" onChange={handleFotoChange} />
                    <div className="form-text">Arrastra y suelta una imagen o usa la cámara del dispositivo.</div>
                  </>
                ) : (
                  <div className="d-flex align-items-start gap-3 justify-content-center">
                    <img src={preview} alt="Preview" style={{ width: 140, height: 140, objectFit: 'cover', borderRadius: 8, border: '1px solid #ddd' }} />
                    <div className="d-flex flex-column gap-2">
                      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => document.getElementById('inputFotoHidden').click()}>Cambiar</button>
                      <button type="button" className="btn btn-sm btn-outline-danger" onClick={limpiarFoto}>Quitar</button>
                    </div>
                  </div>
                )}
                <input id="inputFotoHidden" className="d-none" type="file" accept="image/*" onChange={handleFotoChange} />
              </div>
              {fotoError && <div className="text-danger small mt-1">{fotoError}</div>}
            </div>
            <div className="col-12 col-md-6">
              <input className="form-control" placeholder="Nombre" value={nuevoAcusado.nombre} onChange={e => setNuevoAcusado({ ...nuevoAcusado, nombre: e.target.value })} />
            </div>
            <div className="col-12 col-md-6">
              <input className="form-control" placeholder="Cédula" value={nuevoAcusado.cedula} onChange={e => setNuevoAcusado({ ...nuevoAcusado, cedula: e.target.value })} />
            </div>
            <div className="col-12 col-md-6">
              <select className="form-select" value={nuevoAcusado.grado} onChange={e => setNuevoAcusado({ ...nuevoAcusado, grado: e.target.value })}>
                <option value="">Selecciona grado</option>
                {grados.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div className="col-12 col-md-6">
              <input className="form-control" placeholder="Unidad" value={nuevoAcusado.unidad} onChange={e => setNuevoAcusado({ ...nuevoAcusado, unidad: e.target.value })} />
            </div>
            <div className="col-12 col-md-6">
              <input className="form-control" placeholder="Dirección" value={nuevoAcusado.direccion} onChange={e => setNuevoAcusado({ ...nuevoAcusado, direccion: e.target.value })} />
            </div>
            
            <div className="col-12 col-md-6">
              <select className="form-select" value={nuevoAcusado.estado_civil} onChange={e => setNuevoAcusado({ ...nuevoAcusado, estado_civil: e.target.value })}>
                <option value="">Estado civil</option>
                <option value="soltero">Soltero/a</option>
                <option value="casado">Casado/a</option>
                <option value="divorciado">Divorciado/a</option>
                <option value="viudo">Viudo/a</option>
              </select>
            </div>
            <div className="col-6 col-md-3">
              <input className="form-control" type="date" placeholder="Fecha de nacimiento" value={nuevoAcusado.fecha_nacimiento} onChange={e => {
                const fecha = e.target.value;
                const edad = calcularEdad(fecha);
                setNuevoAcusado({ ...nuevoAcusado, fecha_nacimiento: fecha, edad });
              }} />
            </div>
            <div className="col-6 col-md-3">
              <input className="form-control" type="number" min="0" placeholder="Edad" value={nuevoAcusado.edad} readOnly />
            </div>
            <div className="col-12">
              {error && <div className="alert alert-danger py-2">{error}</div>}
              <button className="btn btn-primary w-100" type="submit" disabled={saving}>
                {saving ? 'Guardando...' : 'Agregar Acusado'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
